import React, { Fragment, useCallback, useContext, useEffect, useReducer, useState, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useIdleTimer } from "react-idle-timer";
import { Mui, PageHeading, PageHeadingContext, usePrevious } from "@osu/react-ui";
import { Auth } from "aws-amplify";
import { isEmpty, pick } from "lodash";
import { AppContext } from "../../App/context";
import ErrorAlert from "../../Common/components/ErrorAlert";
import { ACTION_STATUS } from "../../util/constants";
import { getInitialStepState, stepReducer, steps, stepKeys, getStep, numOfStepsToComplete } from "../steps";
import useStyles from "../styles";
import Header from "./Header";
import usePrefilledSearchParams from "../hooks/usePrefilledSearchParams";
import ErrorList from "./ErrorList";
import { getDefaultStepState, getStepIndexAndReroute } from "../transform";
import { createStepHeadingTitle } from "../util/functions";
import { htmlIdentifiers } from "../util/constants";
import FormWrapper from "./FormWrapper";

const elevation = 4

export const createInputId = (step) => `active-form-step-${step}-id`
export const createLabelId = (step) => createInputId(step) + "-label"

export default function Form(props = {}) {
    const { submitRequest, fileUpload, getConfig, logout, resetFileUpload, resetRequest, setFileUploadProgress, uploadFile, user, userStatus,
        requestStatus, config, configStatus, validAudiences } = props;
    const { skipToHeadingRef } = useContext(AppContext)
    const headingRef = skipToHeadingRef
    const location = useLocation()
    const { setPageTitle, pageTitle } = useContext(PageHeadingContext);
    const isRequestResolving = ([ACTION_STATUS.LOADING].includes(requestStatus));
    const hasPageError = (configStatus === ACTION_STATUS.ERROR);
    const isRequestComplete = (requestStatus === ACTION_STATUS.SUCCESS);
    const hasRequestError = (requestStatus === ACTION_STATUS.ERROR)
    const isFileUploading = (fileUpload?.status === ACTION_STATUS.LOADING);
    const { searchParamsProvided, initialState: searchParamState, errors: searchParamErrors } = usePrefilledSearchParams()
    const { handleSignIn } = useContext(AppContext)
    const history = useHistory();
    const [useSearchParams, setUseSearchParams] = useState(true);
    const [errorProps, setErrorProps] = useState(null);
    const [stepState, dispatchStepState] = useReducer(stepReducer, getInitialStepState());    
    const [activeStep, setActiveStepState] = useState(getDefaultStepState(location?.hash, stepState));
    const [isIdleTimerStarted, setIdleTimerStarted] = useState(false);
    const [startIdleTimer, setStartIdleTimer] = useState(false);
    const [isSessionExpired, setSessionExpired] = useState(false);
    const [focusableId, setFocusableId] = useState("")
    const classes = useStyles({ elevation });
    const errorWasShown = useRef()
    const parentComponentRef = useRef()
    const previousUser = usePrevious(user)
    const pushHistory = history.push
    const historyListen = history.listen
    const contactInformation = stepState[stepKeys.CONTACT_INFORMATION].values;

    const updateHistoryWithStep = useCallback((stepId) => {
        const { key: newStepKey } = getStep(stepId) ?? {}
        const searchParams = new URLSearchParams(history?.location?.search) ?? {};
        const audience = searchParams.get("audience");
        const topic = searchParams.get("topic");
        const subtopic = searchParams.get("subtopic");
        let cleanedSearch = new URLSearchParams({})
        if(audience) cleanedSearch.set("audience", audience)
        if(topic) cleanedSearch.set("topic", topic)
        if(subtopic) cleanedSearch.set("subtopic", subtopic)
        
        newStepKey && pushHistory({
            hash: `#${newStepKey}`,
            search: cleanedSearch.toString() || ""
        })
    }, [history?.location?.search, pushHistory])

    const setActiveStep = useCallback((stateParms) => {
        if(typeof stateParms === 'function') {
            return setActiveStepState((existing) => {
                const updatedStep = stateParms(existing)
                updateHistoryWithStep(updatedStep)
                return updatedStep
            })
        } 
        updateHistoryWithStep(stateParms)
        setActiveStepState(stateParms)
    }, [updateHistoryWithStep])

    useEffect(() => {
        return historyListen((loc, action) => {
            const lockState = isRequestResolving || isRequestComplete || hasRequestError
            if(action === "POP" &&  !lockState) {
                setActiveStep(getDefaultStepState(loc.hash, stepState))
            }
        })
    }, [historyListen, isRequestResolving, isRequestComplete, hasRequestError, setActiveStep, stepState])
        
    const { Component, index: stepIndex, key: stepKey } = steps.find(({ index }) => {
        return (index === activeStep)
    }) ?? {};

    const isReviewStep = (stepKey === stepKeys.REVIEW);
    const stepRoRenderAsActive = activeStep
    const currentStepDetails = getStep(stepRoRenderAsActive)
    const login = async (osuLogin = false) => {
        if(osuLogin === true) {
            if(user) {
                setActiveStep(0);
            } else {
                handleSignIn();
            }
        } else {
            const userNeedsCleared = user && Object.values(user).some((value) => !!value)
            let authUser;
            if(userNeedsCleared) {
                authUser = user
            } else {
                try {
                    authUser = await Auth.currentAuthenticatedUser();
                } catch(error) {
                    console.log(error); // the user is not authenticated
                }
            }
            if(authUser) {
                logout(location.search, `#${stepKeys.CONTACT_INFORMATION}`); // logout and bring the user back to the Contact Information page
            }
            
            const stateNeedsCleared = contactInformation && Object.values(contactInformation).some((value) => !!value)
            if(stateNeedsCleared) {
                dispatchStepState({ type: "RESET_WITH_UNAUTH" });
            }
            
            setActiveStep(0);
        }
    };
    const onIdle = () => {
        if(isIdleTimerStarted === true) {
            setSessionExpired(true); // open the session dialog
            setTimeout(() => setSessionExpired(false), 300000); // close the session dialog automatically when the timout elapses
            logout();
        }
    };
    const idleTimer = useIdleTimer({
        onIdle,
        startOnMount: false,
        startManually: true,
        stopOnIdle: true,
        timeout: 60000 * (parseInt(process.env.REACT_APP_TIMEOUT) || 120)
    });
    const onSessionDialogClose = () => setSessionExpired(false); // close the session dialog
    const getDefaultStep = useCallback((currentUser) => {
        const loginStepComplete = stepState?.[stepKeys.LOGIN]?.isComplete
        if(currentUser || loginStepComplete) {
            return 0
        }
        return -1
    }, [stepState]);
    const updateStepState = (stepKey, stepState) => {
        dispatchStepState({ type: "UPDATE", payload: { stepKey, stepState } });
    };
    const createNewRequest = () => {
        dispatchStepState({ type: "RESET" });
        resetRequest();
        setUseSearchParams(true);
        logout(); // logout the user to allow for the active step to be Login
        setActiveStep(-1);
        history.replace({ search: "" }); // clear query parameters
    };
    const updateContactInformation = useCallback((currentUser) => {
        if(currentUser) { // update with user info
            dispatchStepState({ 
                type: "UPDATE", 
                payload: {
                    stepKey: stepKeys.CONTACT_INFORMATION,
                    stepState: { values: pick(currentUser, ["email", "firstName", "lastName"]), isComplete: true }
                }
            });
        } else {
            dispatchStepState({ type: "RESET" }); // reset all state since some options don't apply to unauthenticated users
        }
    }, [dispatchStepState]);
    const onFileUpload = (file) => {
        const email = stepState[stepKeys.CONTACT_INFORMATION].values.email;
        const uploadName = encodeURIComponent(`HELP_OSU-${email}-${new Date().getTime()}-${file.name}`);
        uploadFile(file, uploadName);
    };

    // when the page is reloaded from logout, navigate to the appropriate step based on the location hash
    useEffect(() => {
        if(history.action === "POP" && location.hash) {
            const rerouteingInfo = getStepIndexAndReroute(location.hash, stepState)
            if(rerouteingInfo.redirect !== location.hash) {
                history.replace({ hash: rerouteingInfo.redirect })
            } else if(activeStep !== rerouteingInfo.index) {
                setActiveStep(rerouteingInfo.index)
            }
        }
    }, [activeStep, history, location.hash, setActiveStep, stepState]);

    // when the user changes, set the active step, update the contact information, and indicate whether the idle timer should be started
    useEffect(() => {
        const userChanged = previousUser?.osuid !== user?.osuid
        if(userChanged) {
            setActiveStep(getDefaultStep(user));
            updateContactInformation(user);
            setStartIdleTimer((user ? true : false));
        }
    }, [getDefaultStep, previousUser?.osuid, setActiveStep, updateContactInformation, user]);

    // when the idle timer should be started, start the idle timer
    useEffect(() => {
        if(startIdleTimer === true && isIdleTimerStarted === false && isSessionExpired === false) {
            idleTimer.start();
            setIdleTimerStarted(true);
        }
    }, [idleTimer, isIdleTimerStarted, isSessionExpired, startIdleTimer]);

    // when the session expires, indicate that the idle timer is stopped and reset the idle timer
    useEffect(() => {
        if(isSessionExpired === true && isIdleTimerStarted === true) {
            setIdleTimerStarted(false);
            idleTimer.reset();
        }
    }, [idleTimer, isIdleTimerStarted, isSessionExpired]);

    // when creating the request fails, clear the error when leaving the Review page
    useEffect(() => {
        if(hasRequestError === true && !isReviewStep) resetRequest();
    }, [hasRequestError, isReviewStep, resetRequest]);

    useEffect(() => {
        const stepHeadingInfo = createStepHeadingTitle({ label: currentStepDetails?.label, step: stepRoRenderAsActive + 1 })
        let newT = stepHeadingInfo?.label
        if(stepHeadingInfo?.stepOf) {
            newT += ` (${stepHeadingInfo?.stepOf})`
        }

        if(newT && typeof newT === 'string' && (pageTitle !== newT)) {
            setPageTitle(newT)
        }

    }, [setPageTitle, stepRoRenderAsActive, currentStepDetails?.label, pageTitle])

    useEffect(() => {
        if(searchParamsProvided) {
            dispatchStepState({
                type: "SEARCH_PARMS",
                payload: {
                    salesforceRoutingId: searchParamState?.salesforceRoutingId, 
                    selectionMappingId: searchParamState?.selectionMappingId, 
                    subtopic: searchParamState?.subtopic, 
                    topic: searchParamState?.topic, 
                    unit: searchParamState?.unit, 
                    audience: searchParamState?.audience,
                    service: searchParamState?.service
                }
            })
        }
    }, [searchParamsProvided, searchParamState?.audience, searchParamState?.salesforceRoutingId, searchParamState?.selectionMappingId, searchParamState?.subtopic, searchParamState?.topic, searchParamState?.unit, searchParamState?.service, searchParamErrors])

    useEffect(() => {
        if(!errorWasShown?.current && searchParamErrors?.length && currentStepDetails?.key === stepKeys.REQUEST) {
            setErrorProps({
                title: "Alert",
                message: (() => {
                    return (
                        <div>
                            The following options provided by the URL are not available:
                            <ErrorList errors={searchParamErrors} />
                        </div>
                    )
                })()
            });
            errorWasShown.current = true
        }
    }, [searchParamErrors, currentStepDetails?.key])

    const showLogoutButton = (![/* isPageLoading,  */hasPageError].includes(true) && user);
    const { isComplete: isStepComplete } = stepState[stepKey] ?? {};
    let stepContext = stepKey === stepKeys.REVIEW ? stepState : stepState[stepKey];
    const componentProps = { 
        fileUpload, login, resetFileUpload, setErrorProps, setFileUploadProgress, setUseSearchParams, stepKey, stepState: stepContext, updateStepState,
        uploadFile: onFileUpload, user, userStatus, useSearchParams, config, configStatus, requestStatus, elevation, validAudiences
    };
    const showLoader = !hasPageError && isRequestResolving
    const showStepComponent = !isRequestResolving && !hasPageError
    const formId = "osu-form-submission-identifier-" + activeStep
    const formControllerProps = {
        type: "submit",
        form: formId,
        "aria-controls": formId,
    }
    let formButton = false, onSubmit = false
    if(isReviewStep) {
        formButton =  <Mui.Button className="margin-top-2" {...formControllerProps} id={htmlIdentifiers.submit} variant="contained" color="primary">Submit</Mui.Button> 
        onSubmit = (e) => {
            submitRequest(stepState, user)
            dispatchStepState({ type: "SET_COMPLETED", payload: { stepKey: stepKeys.REVIEW, isComplete: true } });
        }
    } else if (stepIndex > -1) {
        const  disabled= isFileUploading
        onSubmit = (e) => {
            e.preventDefault();
            if(isStepComplete) {
                setActiveStep((existingStepToActivate) => {    
                    let stepToActivate = (numOfStepsToComplete === existingStepToActivate) ? existingStepToActivate : existingStepToActivate + 1
                    setFocusableId(createLabelId(stepToActivate))
                    
                    return stepToActivate
                })
                if(headingRef?.current) {
                    headingRef.current.focus()
                }
            }
            return {
                stepKey,
                isStepComplete
            }
        }
        const confirmedNextLabelID = parentComponentRef?.current?.querySelector("#" + htmlIdentifiers.nextLabel)?.id
        formButton = <Mui.Button 
            {...formControllerProps} 
            disabled={disabled}
            id={htmlIdentifiers.continue} 
            variant="outlined" 
            color="primary"
            className={!isStepComplete ? 'disabled-accessible-button' : undefined}
            aria-describedby={confirmedNextLabelID || undefined}
        >Continue</Mui.Button>
    }

    const showCreateNewRequestButton = isRequestComplete && stepKey === stepKeys.REVIEW

    return (
        <div ref={parentComponentRef}>
            {errorProps && <ErrorAlert {...errorProps} /> }
            <Mui.Card display="flex"  elevation={elevation} className={classes.card}>
                <Mui.Box display="flex" flexWrap="wrap" justifyContent="space-between">
                <Mui.Box display="flex" flexDirection="column">
                    <PageHeading variant="h3" className="display-flex flex-direction-column" />
                    </Mui.Box>
                    <Mui.Box display="flex" flexWrap="wrap">
                        <Mui.Typography component="div" variant="body2" className={classes.userName}>
                            {!isEmpty(contactInformation) && `${contactInformation.firstName ?? ""} ${contactInformation.lastName ?? ""}`}
                            {showLogoutButton && ` | ` }
                        </Mui.Typography>
                        {showLogoutButton && <Mui.Button color="primary" className={classes.logoutButton} onClick={() => logout()}>Log Out</Mui.Button>}
                    </Mui.Box>
                </Mui.Box>
                {/* {isPageLoading &&
                    <Mui.Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" className={classes.status}>
                        <Mui.CircularProgress aria-describedby="pageLoading" />
                        <Mui.Typography id="pageLoading" variant="body1" aria-live="assertive">Loading, please wait...</Mui.Typography>
                    </Mui.Box>
                } */}
                <Header
                    headingRef={headingRef}
                    checkStateByKey={(key) => {
                    return stepState[key];
                    }}
                    setActiveStep={setActiveStep}
                    elevation={elevation}
                    activeStep={activeStep}
                    stepRoRenderAsActive={stepRoRenderAsActive}
                    isRequestResolving={isRequestResolving}
                    isReviewStep={isReviewStep}
                    isRequestComplete={isRequestComplete}
                    errorSubmitting={hasRequestError}
                    hasPageError={hasPageError}
                />           
                {hasPageError &&
                    <Mui.Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" className={classes.status}>
                        <Mui.Typography variant="body1">There was an error loading the page.</Mui.Typography>
                        <Mui.Button variant="contained" color="primary" aria-label="Reload the Page" onClick={(event) => {
                            getConfig()

                        }}>Reload</Mui.Button>
                    </Mui.Box>

                } 
                <Fragment>
                    <Mui.Box data-testid="pageLoading" display="flex" flexDirection="column" justifyContent="center" alignItems="center" className={isRequestResolving ? classes.status : null}>
                        {(showLoader) &&<Mui.CircularProgress aria-describedby="pageLoading" />  }
                        <Mui.Typography id="pageLoading" variant="body1" aria-live="assertive">{showLoader && "Submitting request, please wait..."}</Mui.Typography>
                    </Mui.Box>
                </Fragment>
                {showStepComponent &&
                    <Fragment>
                        {/* <div className="margin-bottom-2" role="group" aria-labelledby={createLabelId(stepRoRenderAsActive)}> */}
                          <FormWrapper focusableId={focusableId} formId={formButton && formId} onSubmit={onSubmit}>
                            <Component {...componentProps} />
                          </FormWrapper>
                        {/* </div> */}
                        <Mui.Box flexDirection={showCreateNewRequestButton ? "row" : "row-reverse"} display="flex" justifyContent="space-between" marginTop="auto">
                            {showCreateNewRequestButton 
                                && <Mui.Button className="margin-top-2" color="primary" onClick={createNewRequest}>Create a New Request</Mui.Button> 
                            }
                        {!isRequestComplete 
                            && <Fragment>
                                    {formButton}
                                    {
                                    (stepIndex >= 0) &&
                                        <Mui.Button id={htmlIdentifiers.back} color="primary" onClick={(e) => {
                                            setActiveStep((existingStepToActivate) => {
                                                const stepToActivate = existingStepToActivate - 1
                                                setFocusableId(createLabelId(stepToActivate))

                                                return stepToActivate
                                            })
                                            if(headingRef?.current && document.activeElement !== headingRef.current) {
                                                headingRef.current.focus()
                                            }
                                        }}>Back</Mui.Button>
                                    }
                                </Fragment>
                        }
                        </Mui.Box>
                    </Fragment>
                }
            </Mui.Card>
            <Mui.Dialog open={isSessionExpired} onClose={onSessionDialogClose} aria-labelledby="sessionDialogTitle" aria-describedby="sessionDialogText">
                <Mui.DialogTitle id="sessionDialogTitle">Session Expired</Mui.DialogTitle>
                <Mui.DialogContent dividers>
                    <Mui.DialogContentText id="sessionDialogText">Your session has expired.</Mui.DialogContentText>
                </Mui.DialogContent>
                <Mui.DialogActions>
                    <Mui.Button variant="outlined" color="primary" onClick={onSessionDialogClose}>Close</Mui.Button>
                </Mui.DialogActions>
            </Mui.Dialog>
        </div>
    );
}